import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button } from "@/src/components/atom/Button";
import Typo from "@/src/components/atom/Typo";
import iconCard from "@/src/assets/icons/icon-card.svg";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import { localstorageKeySet } from "@/src/constant/localstorage";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import {
  useGetPaymentsQuery,
  useGetPlansQuery,
  useLazyGetSubscriptionQuery,
  useLazyGetSubscriptionsQuery,
} from "@/src/store/apis/subscription";
import { isNull, isUndefined } from "@/src/utils/is";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import FreeCodeDialog from "./components/dialog/FreeCodeDialog";
import UnsubscribeCompletedDialog from "./components/dialog/UnsubscribeCompletedDialog";
import UnsubscribeDialog, {
  UnsubscribeType,
} from "./components/dialog/UnsubscribeDialog";
import AddPaymentMethodsDialog from "./components/dialog/AddPaymentMethodsDialog";
import MonthlyUsageHistory from "./components/MonthlyUsageHistory";
import PaymentManagement from "./components/PaymentManagement";
import SubscriptionInfo from "./components/SubscriptionInfo";
import SubscriptionStatusCallout from "./components/SubscriptionStatusCallout";
import { renderSubscriptionStatusBadge } from "./utils/renderSubscriptionStatusBadge";
import { getSubscriptionDisplayType } from "./utils/subscriptionHelpers";

enum AlertDialogState {
  NULL,
  UNSUBSCRIBE,
}

export enum SubscriptionDialogState {
  NULL,
  FREE_CODE,
  UNSUBSCRIBE,
  PLAN_CHANGE,
  EXPIRATION,
  IMMEDIATE,
  PAYMENT_METHOD_ADD,
}

function SubscriptionManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const loadingTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();

  const [dialogState, setDialogState] = useState<SubscriptionDialogState>(
    SubscriptionDialogState.NULL,
  );
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL,
  );
  const [subscriptionIds, setSubscriptionIds] = useState<number[]>([]);
  const [freeCodeIds, setFreeCodeIds] = useState<number[]>([]);
  const [unsubscribeType, setUnsubscribeType] =
    useState<UnsubscribeType | null>(null);
  const [hasPaymentMethodsFromChild, setHasPaymentMethodsFromChild] =
    useState<boolean>(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);

  // 구독 정보를 먼저 가져와서 FREE 타입인지 확인
  const [
    getSubscription,
    {
      isFetching,
      planType,
      subscriptionStatus,
      nextPlan,
      isStable,
      retractFreeCodeReason,
      paymentCycle,
      subscriptionNo,
      subscriptionType,
    },
  ] = useLazyGetSubscriptionQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isFetching || isError || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        isFetching,
        isStable,
        planType: currentData?.plan.planType,
        subscriptionStatus: currentData?.subscriptionStatus,
        nextPlan: currentData?.nextPlan,
        freeCodeList: currentData?.freeCodeList,
        retractFreeCodeReason: currentData?.retractFreeCodeReason,
        paymentCycle: currentData?.paymentCycle,
        subscriptionNo: currentData?.subscriptionNo,
        subscriptionType: currentData?.subscriptionType,
      };
    },
  });

  // FREE 구독이 아닌 경우에만 결제 정보 조회
  useGetPaymentsQuery(
    { page: 1, pageSize: 1 },
    {
      skip: subscriptionType === "FREE", // FREE 구독일 때는 API 호출 건너뛰기
    },
  );

  // PaymentManagement 컴포넌트에서 결제수단 정보를 받아옴
  const hasPaymentMethods = hasPaymentMethodsFromChild;

  useGetPlansQuery({ planType: "NORMAL" });
  useGetCommonCodeViaCodeNameQuery({
    codeName: "COUNTRY",
  });

  const session = useGetSessionQuery(undefined);

  const [getSubscriptions, { isError }] = useLazyGetSubscriptionsQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isFetching || isError || isUndefined(currentData);
      const isStable = !isUnstable;

      return { currentData, isSubscriptionsStable: isStable, isError };
    },
  });

  const isManagerType =
    session.currentData?.row.exporterUserType === "MANAGER" ||
    session.currentData?.row.exporterUserType === "MIDDLE_MANAGER";

  const handleUnsubscribeClick = () => {
    setAlertDialogState(AlertDialogState.UNSUBSCRIBE);
  };
  const handleFreeCodeClick = () => {
    setDialogState(SubscriptionDialogState.FREE_CODE);
  };
  const handlePlanChangeClick = () => {
    navigate(EXPORTER_PRIVATE_PATH.PLAN_CHANGE);
  };
  const handlePaymentMethodClick = () => {
    setDialogState(SubscriptionDialogState.PAYMENT_METHOD_ADD);
  };
  const handlePlanSubscriptionClick = () => {
    navigate(EXPORTER_PRIVATE_PATH.SUBSCRIPTION_PLAN);
  };
  const handleUnsubscribeConfirmClick = () => {
    setAlertDialogState(AlertDialogState.NULL);
    setDialogState(SubscriptionDialogState.UNSUBSCRIBE);
  };

  const renderAlertDialog = () => {
    if (alertDialogState === AlertDialogState.UNSUBSCRIBE) {
      return (
        <AlertDialog
          open
          title={t("subscriptionManagement:subscription.unsubscribePlan")}
          onOpenChange={() => setAlertDialogState(AlertDialogState.NULL)}
          cancelText={t("subscriptionManagement:subscription.button.cancel")}
          okText={t("subscriptionManagement:subscription.button.ok")}
          onOk={handleUnsubscribeConfirmClick}
        >
          <WhiteSpaceTypo color="gray5" typoType="b8r">
            {t(
              "subscriptionManagement:subscription.unsubscribePlanDescription",
            )}
          </WhiteSpaceTypo>
        </AlertDialog>
      );
    }
  };

  const renderUnsubscribeCompletedDialog = () => {
    if (unsubscribeType) {
      // 기간 만기 해지
      if (unsubscribeType === "EXPIRATION") {
        return (
          <UnsubscribeCompletedDialog
            open
            onOpenChange={() => {
              setDialogState(SubscriptionDialogState.NULL);
              setUnsubscribeType(null);
            }}
            unsubscribeType={unsubscribeType}
          />
        );
      }

      // 즉시 해지
      if (unsubscribeType === "IMMEDIATE") {
        return (
          <UnsubscribeCompletedDialog
            open
            onOpenChange={() => {
              setDialogState(SubscriptionDialogState.NULL);
              setUnsubscribeType(null);
            }}
            unsubscribeType={unsubscribeType}
          />
        );
      }
    }
  };

  const renderDialog = () => {
    // 무료 기간 코드
    if (dialogState === SubscriptionDialogState.FREE_CODE) {
      return (
        <FreeCodeDialog
          open
          onOpenChange={() => setDialogState(SubscriptionDialogState.NULL)}
        />
      );
    }
    // 구독 해지
    if (dialogState === SubscriptionDialogState.UNSUBSCRIBE) {
      return (
        <UnsubscribeDialog
          open
          onOpenChange={() => setDialogState(SubscriptionDialogState.NULL)}
          onSave={(type) => {
            setUnsubscribeType(type);
          }}
        />
      );
    }
    // 결제 수단 등록
    if (dialogState === SubscriptionDialogState.PAYMENT_METHOD_ADD) {
      return (
        <AddPaymentMethodsDialog
          open
          onOpenChange={() => setDialogState(SubscriptionDialogState.NULL)}
        />
      );
    }
  };

  const renderBadgeContainer = () => {
    const isUnstableNextPlan = isNull(nextPlan);
    const renderPlanButton = () => {
      if (!isUnstableNextPlan) return null;

      const isSubscriptionInProgress = subscriptionStatus === "PROGRESS";

      return (
        <Button
          buttonSize={32}
          disabled={isManagerType}
          onClick={
            isSubscriptionInProgress
              ? handlePlanChangeClick
              : handlePlanSubscriptionClick
          }
        >
          {t(
            isSubscriptionInProgress
              ? "subscriptionManagement:subscription.button.planChange"
              : "subscriptionManagement:subscription.button.planSubscription",
          )}
        </Button>
      );
    };

    // 구독해지 예정, 구독해지
    if (subscriptionStatus === "CANCELED") {
      return (
        <BadgeContainer $justifyContent="space-between">
          {renderSubscriptionStatusBadge({
            type: subscriptionStatus,
            t,
            size: "L",
            subscriptionType,
          })}
        </BadgeContainer>
      );
    }

    return (
      <BadgeContainer $justifyContent="space-between">
        {renderSubscriptionStatusBadge({
          type: getSubscriptionDisplayType(
            subscriptionStatus,
            subscriptionType,
            planType,
          ),
          t,
          size: "L",
          subscriptionType,
        })}
        <PlanButtonContainer $gap={8}>
          {hasPaymentMethods === false && (
            <Button
              buttonColor="blue"
              buttonGrade="secondary"
              buttonSize={32}
              disabled={isManagerType}
              onClick={handlePaymentMethodClick}
            >
              <ButtonContent>
                <CardIcon src={iconCard} alt="card" />
                {t(
                  "subscriptionManagement:subscription.button.registerPaymentMethod",
                )}
              </ButtonContent>
            </Button>
          )}

          <Button
            buttonColor="blue"
            buttonGrade="secondary"
            buttonSize={32}
            disabled={isManagerType}
            onClick={handleFreeCodeClick}
          >
            {t("subscriptionManagement:subscription.button.freeTrialCode")}
          </Button>

          {renderPlanButton()}
        </PlanButtonContainer>
      </BadgeContainer>
    );
  };

  const renderSubscriptionContent = () => {
    if (!isFetching) {
      if (isError) {
        return (
          <SubscriptionStatusCallout
            subscriptionIds={subscriptionIds}
            onSubscriptionIdsChange={setSubscriptionIds}
            onPlanSubscriptionClick={handlePlanSubscriptionClick}
          />
        );
      }

      if (isStable) {
        return (
          <>
            {renderBadgeContainer()}
            <Flex $direction="column" $gap={16}>
              {/* 구독 Callout */}
              <SubscriptionStatusCallout
                subscriptionIds={subscriptionIds}
                onSubscriptionIdsChange={setSubscriptionIds}
                onPlanSubscriptionClick={handlePlanSubscriptionClick}
              />
              {/* 무료기간 코드 회수 */}
              {!!retractFreeCodeReason && (
                <SubscriptionStatusCallout
                  subscriptionIds={subscriptionIds}
                  onSubscriptionIdsChange={setSubscriptionIds}
                  freeCodeIds={freeCodeIds}
                  onFreeCodeIdsChange={setFreeCodeIds}
                  type={"RETRACT_FREE_CODE"}
                  onPlanSubscriptionClick={handlePlanSubscriptionClick}
                />
              )}

              {/**
               * - 구독 정보
               * - 결제 관리
               * - 구독 내역
               * */}
              <SubscriptionInfo />
              <PaymentManagement
                subscriptionStatus={subscriptionStatus}
                onPaymentMethodsChange={setHasPaymentMethodsFromChild}
              />
              <MonthlyUsageHistory />
              {/* 구독 해지 버튼 */}
              {subscriptionStatus !== "CANCELED" && (
                <ButtonContainer>
                  <Button
                    buttonColor="black"
                    buttonGrade="tertiary"
                    disabled={isManagerType}
                    onClick={handleUnsubscribeClick}
                  >
                    {t(
                      "subscriptionManagement:subscription.button.unsubscribe",
                    )}
                  </Button>
                </ButtonContainer>
              )}
            </Flex>
          </>
        );
      }
    }
  };

  useEffect(() => {
    if (isManagerType) {
      if (session.currentData?.row.exporterUserMainFieldType === "BUYER") {
        navigate(IMPORTER_PRIVATE_PATH.HOME);
        return;
      }
      navigate(EXPORTER_PRIVATE_PATH.HOME);
      return;
    }
  }, [
    isManagerType,
    navigate,
    session.currentData?.row.exporterUserMainFieldType,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getSubscriptions({
          page: 1,
          pageSize: 10,
        }).unwrap();

        if (res.rows) {
          await getSubscription({ id: res.rows[0].id }).unwrap();
        }

        // 콜아웃 닫은 id ids배열
        const localStorageFreeCodeIds = localStorage.getItem(
          localstorageKeySet.FREE_CODE_IDS,
        )
          ? (JSON.parse(
              localStorage.getItem(localstorageKeySet.FREE_CODE_IDS) as string,
            ) as string[])
          : [];
        const localStorageSubscriptionIds = localStorage.getItem(
          localstorageKeySet.SUBSCRIPTION_IDS,
        )
          ? (JSON.parse(
              localStorage.getItem(
                localstorageKeySet.SUBSCRIPTION_IDS,
              ) as string,
            ) as string[])
          : [];

        setFreeCodeIds(localStorageFreeCodeIds.map((item) => Number(item)));
        setSubscriptionIds(
          localStorageSubscriptionIds.map((item) => Number(item)),
        );
      } catch (e: any) {
        if (e.status !== 404) {
          const message = Array.isArray(e.data?.message)
            ? e.data?.message[0]
            : e.data?.message;
          alert.showAlert({
            type: "error",
            message: message,
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentCycle === "ANNUALLY" && subscriptionNo) {
      (async () => {
        try {
          await getSubscriptions({
            annualPaymentInstallment: 1,
            subscriptionNo,
          }).unwrap();
        } catch (error) {
          console.error("Failed to fetch subscriptions:", error);
        }
      })();
    }
  }, [getSubscriptions, paymentCycle, subscriptionNo]);

  useEffect(() => {
    if (isFetching) {
      const setTimeoutId = setTimeout(() => {
        handleContentLoadingOn();
      }, 300);

      loadingTimeoutId.current = setTimeoutId;
    } else {
      if (loadingTimeoutId.current) {
        clearTimeout(loadingTimeoutId.current);
      }
      handleContentLoadingOff();
    }
  }, [handleContentLoadingOff, handleContentLoadingOn, isFetching]);

  useEffect(() => {
    return () => {
      handleContentLoadingOff();
    };
  }, [handleContentLoadingOff]);

  return (
    <ExporterMainLayout
      breadcrumb={[
        t("sideNav:workspaceSettings"),
        t("sideNav:subscription"),
      ]}
      pageTitle={t("sideNav:subscription")}
    >
      {renderSubscriptionContent()}
      {renderUnsubscribeCompletedDialog()}
      {renderAlertDialog()}
      {renderDialog()}
      {/* 독립적인 결제 수단 등록 모달 */}
      {showPaymentMethodDialog && (
        <AddPaymentMethodsDialog
          open={showPaymentMethodDialog}
          onOpenChange={setShowPaymentMethodDialog}
          onSuccess={() => {
            // 결제 수단 등록 성공 시 모달 닫고 구독 모달 다시 열기
            setShowPaymentMethodDialog(false);
            setDialogState(SubscriptionDialogState.PLAN_CHANGE);
          }}
        />
      )}
    </ExporterMainLayout>
  );
}

export default SubscriptionManagementPage;

const Flex = styled.div<{
  $direction?: CSSProperties["flexDirection"];
  $alignItems?: CSSProperties["alignItems"];
  $justifyContent?: CSSProperties["justifyContent"];
  $gap?: number;
}>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  align-items: ${({ $alignItems }) => $alignItems};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  gap: ${({ $gap }) => $gap || 0}px;
`;

const BadgeContainer = styled(Flex)`
  padding-bottom: 18px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
`;

const WhiteSpaceTypo = styled(Typo)`
  white-space: pre-wrap;
`;

const PlanButtonContainer = styled(Flex)`
  margin-left: auto;
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const CardIcon = styled.img`
  width: 16px;
  height: 16px;
`;
